const NEWSLETTER_CLOSED_DAYS = 30;
const NEWSLETTER_SUBSCRIBED_DAYS = 365;

class NewsletterPopup extends ModalDialog {
  constructor() {
    super();
    this.init();
    this.attachEventListeners();
  }

  attachEventListeners() {
    this.querySelectorAll(".js-button-close").forEach((btn) => {
      btn.addEventListener("click", this.closeNewsletter.bind(this));
    });

    if (this.form) {
      this.form.addEventListener("submit", this.onSubscribe.bind(this));
    }

    this.observeCartDrawer();
    this.attachMatch();
  }

  init() {
    this.delay = this.dataset.delay * 1000;
    this.closed = getCookie("newsletter-closed");
    this.subscribed = getCookie("newsletter-subscribed");
    this.form = this.querySelector(".js-form");
    this.ageVerificationPopup = document.querySelector("age-verification-popup");
    this.offer = this.querySelector("[data-popup-offer]");
    this.success = this.querySelector("[data-popup-success]");
    this.game = this.querySelector("[data-popup-game]");
    this.grid = this.querySelector("[data-match-grid]");
    this.hint = this.querySelector("[data-game-hint]");
    this.misses = 0;
    this.finished = false;
    this.locked = false;
    this.openCards = [];
    this.cards = [];
  }

  closeNewsletter() {
    setCookie("newsletter-closed", "true", NEWSLETTER_CLOSED_DAYS);
    this.hide();
  }

  connectedCallback() {
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") {
        this.show();
      }
      return;
    }

    this.handlePopupVisibility();
  }

  hasCartItems() {
    if (this.dataset.template === "cart") return true;
    if (Number(this.dataset.cartCount) > 0) return true;

    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer?.classList.contains("is-visible")) return true;

    const counters = document.querySelectorAll(
      ".cart-count-badge, .cart-drawer__title-counter"
    );
    for (const counter of counters) {
      const count = parseInt(counter.textContent, 10);
      if (!Number.isNaN(count) && count > 0) return true;
    }

    return false;
  }

  canShow() {
    return (
      this.closed !== "true" &&
      this.subscribed !== "true" &&
      !this.hasCartItems()
    );
  }

  observeCartDrawer() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    const observer = new MutationObserver(() => {
      if (
        cartDrawer.classList.contains("is-visible") &&
        this.hasAttribute("open")
      ) {
        this.hide();
      }
    });

    observer.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ["class"]
    });
  }

  handlePopupVisibility() {
    if (getCookie("age-verified") !== "true" && this.ageVerificationPopup) {
      this.handleAgeVerificationPopup();
    } else {
      this.showPopupWithDelay();
    }
  }

  handleAgeVerificationPopup() {
    let prevClassState = this.classList.contains("newsletter-popup-is-hidden");

    const observer = new MutationObserver(mutations => {
      const mutation = mutations[0];
      if (mutation.attributeName !== "class") return;

      const currentClassState = mutation.target.classList.contains("newsletter-popup-is-hidden");

      if (prevClassState !== currentClassState) {
        prevClassState = currentClassState;
        if (!currentClassState) {
          this.showPopupWithDelay();
        }
      }
    });

    observer.observe(this, { attributes: true });
  }

  showPopupWithDelay() {
    setTimeout(() => {
      if (this.canShow()) {
        this.show();
      }
    }, this.delay);
  }

  attachMatch() {
    if (!this.grid) return;

    this.cards = Array.from(this.grid.querySelectorAll(".match-card"));
    for (let i = this.cards.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.cards[i], this.cards[j]] = [this.cards[j], this.cards[i]];
    }
    this.cards.forEach((card) => this.grid.appendChild(card));

    this.cards.forEach((card) => {
      card.addEventListener("click", () => this.flipCard(card));
    });
  }

  openCard(card) {
    card.classList.add("is-flipped");
    card.setAttribute("aria-pressed", "true");
  }

  foldCard(card) {
    card.classList.remove("is-flipped");
    card.setAttribute("aria-pressed", "false");
  }

  flipCard(card) {
    if (this.finished || this.locked) return;
    if (card.classList.contains("is-matched")) return;
    if (this.openCards.includes(card)) return;

    this.openCard(card);
    this.openCards.push(card);

    if (this.openCards.length < 3) return;

    this.locked = true;
    const first = this.openCards[0];
    const isMatch = this.openCards.every(
      (openCard) => openCard.dataset.pair === first.dataset.pair
    );

    if (isMatch) {
      this.openCards.forEach((openCard) => openCard.classList.add("is-matched"));
      this.openCards = [];
      this.playWin();
      return;
    }

    if (this.hint) this.hint.hidden = false;

    window.setTimeout(() => {
      this.openCards.forEach((openCard) => this.foldCard(openCard));
      this.openCards = [];
      this.locked = false;
    }, 900);
  }

  playWin() {
    if (this.finished) return;
    this.finished = true;
    this.locked = true;

    if (this.game) this.game.classList.add("is-winning");

    this.cards.forEach((card) => {
      card.classList.add(card.classList.contains("is-matched") ? "is-won" : "is-lost");
    });

    const win = this.querySelector("[data-match-win]");
    if (win) {
      win.hidden = false;
      requestAnimationFrame(() => win.classList.add("is-visible"));
    }

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.setTimeout(() => this.revealOffer("win"), reduceMotion ? 0 : 2400);
  }

  revealOffer(mode) {
    this.finished = true;
    if (this.game) this.game.hidden = true;
    if (this.offer) this.offer.hidden = false;

    const winHeading = this.querySelector("[data-win-heading]");
    const claimHeading = this.querySelector("[data-claim-heading]");
    const winBody = this.querySelector("[data-win-body]");
    const claimBody = this.querySelector("[data-claim-body]");
    const isWin = mode === "win";

    if (winHeading) winHeading.hidden = !isWin;
    if (winBody) winBody.hidden = !isWin;
    if (claimHeading) claimHeading.hidden = isWin;
    if (claimBody) claimBody.hidden = isWin;
  }

  showSuccess() {
    this.classList.add("is-subscribed");
    if (this.success) this.success.hidden = false;
    if (this.offer) this.offer.hidden = true;
    if (this.game) this.game.hidden = true;
  }

  onSubscribe(event) {
    event.preventDefault();
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    setCookie("newsletter-subscribed", "true", NEWSLETTER_SUBSCRIBED_DAYS);
    this.subscribed = "true";

    const submitButton = this.form.querySelector('[type="submit"]');
    if (submitButton) submitButton.disabled = true;

    fetch(this.form.action, {
      method: this.form.method || "POST",
      body: new FormData(this.form),
      headers: { Accept: "text/html" }
    }).catch(() => {});

    this.showSuccess();
    setTimeout(() => this.hide(), 2200);
  }
}

customElements.define("newsletter-popup", NewsletterPopup);
